import type { OrderSteps, PairedOrderSteps } from '../typings'

import { useCallback, useEffect, useState } from 'react'

import { useCloseModal, useConfigureModalController, useModalController } from '@/atoms'
import { useReadOnly } from '@/hooks'
import { useAutoSubmit } from './useAutoSubmit'
import { useSelectedOrderSteps } from './useSelectedOrderSteps'

export type ModalMode = 'empty' | 'single' | 'bulk'
type ModalState = 'ready' | 'pending' | 'invalidForEmptyOrders'
interface ModalData {
  mode: ModalMode
  cloneAlsoPairedStep: boolean
  selectedOrderSteps: OrderSteps
  pairedOrderSteps: PairedOrderSteps
  gotRecurrentOrders: boolean
  allRecurrentOrders: boolean
  cloneEntireRecurrence: boolean
  requestInProgress: boolean
  initialValidationInProgress: boolean
  canDuplicateRecurrences: boolean
}

export const modalId = 'duplicateOrder'

export const useController = () => useModalController<ModalData, ModalState>(modalId)
export const useConfigureController = (orderStepIds: string[]) => {
  const { selectedOrderSteps, pairedOrderSteps } = useSelectedOrderSteps(orderStepIds)
  const { readonly } = useReadOnly()

  const close = useCloseModal()

  const { ids } = selectedOrderSteps

  const getModalMode = useCallback((): ModalMode => {
    if (ids.length > 1) return 'bulk'
    if (ids.length > 0) return 'single'
    return 'empty'
  }, [ids.length])

  const { allRecurrentOrders, gotRecurrentOrders } = Object.values(
    selectedOrderSteps.orderSteps,
  ).reduce<{
    allRecurrentOrders: boolean
    gotRecurrentOrders: boolean
  }>(
    (acc, step) => {
      if (!!step.order.recurrenceId) {
        acc.gotRecurrentOrders = true
      } else {
        acc.allRecurrentOrders = false
      }

      return acc
    },
    { allRecurrentOrders: true, gotRecurrentOrders: false },
  )

  // INITIALIZATION

  const [options] = useState(() => ({
    createInitialState: () => ({
      close,
      data: {
        pairedOrderSteps,
        selectedOrderSteps,
        mode: getModalMode(),
        cloneAlsoPairedStep: true,
        allRecurrentOrders,
        gotRecurrentOrders,
        requestInProgress: false,
        cloneEntireRecurrence: false,
        canDuplicateRecurrences: true,
        initialValidationInProgress: false,
      },
    }),
  }))

  const ctrl = useConfigureModalController<ModalData, ModalState>(modalId, options)
  const {
    data: { mode },
    status,
    update,
    updateData,
  } = ctrl

  // EFFECTS

  // External transaction
  useEffect(() => {
    switch (readonly) {
      case true:
        update({ status: 'pending' })
        break

      case false:
        if (status !== 'pending') return

        update({ status: 'ready' })
        break
    }
  }, [readonly, status, update])

  // Invalid state management
  useEffect(() => {
    updateData({ mode: getModalMode() })
  }, [getModalMode, updateData])

  // Invalid state management
  useEffect(() => {
    if (mode === 'empty') {
      update({ invalid: true, status: 'invalidForEmptyOrders' })
      return
    }

    update({ invalid: false, status: 'ready' })
  }, [mode, update])

  /**
   * If the user selected one or more orders without paireds or, in the selection,
   * all paireds are already present, the modal should submit automatically and not appear.
   */
  useAutoSubmit()

  return ctrl
}
